import React, { useEffect, useRef, useState } from "react";

export default function LocalPreview({
  onStreamChange,
  tileAspect = "4 / 3",
  defaultMirror = true,
  onToggleChat, // 👈 nieuw: chat-toggle meegegeven vanuit buiten
  onMicChange,  // 👈 NIEUW: mic-status doorgeven
}) {

  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [mirrored] = useState(defaultMirror);

  const [showUi, setShowUi] = useState(true);

  // getUserMedia + cleanup
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const constraints = {
          video: camOn,
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        };
        const s = await navigator.mediaDevices.getUserMedia(constraints);

        if (!active) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }

        // oude stream uitzetten
        if (streamRef.current && streamRef.current !== s) {
          try {
            streamRef.current.getTracks().forEach((t) => t.stop());
          } catch {}
        }

        streamRef.current = s;
        onStreamChange?.(s);

        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.play().catch(() => {});
        }
      } catch (e) {
        console.warn("[LocalPreview] getUserMedia failed:", e);
      }
    })();

    return () => {
      active = false;
      const s = streamRef.current;
      if (s) {
        try {
          s.getTracks().forEach((t) => t.stop());
        } catch {}
        streamRef.current = null;
      }
      onStreamChange?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camOn]);

  // mic toggle
  useEffect(() => {
    const s = streamRef.current;
    if (!s) return;
    s.getAudioTracks().forEach((t) => (t.enabled = micOn));
  }, [micOn]);

  // auto-hide UI in tile
  useEffect(() => {
    if (!showUi) return;
    const id = setTimeout(() => setShowUi(false), 2200);
    return () => clearTimeout(id);
  }, [showUi]);

  const wrapperStyle = {
    position: "relative",
    background: "#000",
    borderRight: "1px solid #020617",
    borderBottom: "1px solid #020617",
    height: "100%",
    width: "100%",
    overflow: "hidden",
  };


  const innerStyle = {
    position: "absolute",
    inset: 0,
    background: "#000",
  };

  const videoStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: mirrored ? "scaleX(-1)" : "none",
    display: "block",
    background: "#000",
  };

  const btnCircle = {
    width: 30,
    height: 30,
    borderRadius: 999,
    border: "none",
    background: "rgba(15,23,42,0.78)",
    color: "#e5e7eb",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    fontSize: 16,
    boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
  };

  return (
    <div
      style={wrapperStyle}
      onMouseMove={() => setShowUi(true)}
      onMouseEnter={() => setShowUi(true)}
    >
      <div style={innerStyle}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          disablePictureInPicture
          style={videoStyle}
          aria-label="Local preview"
        />

        {/* Mic / Cam knoppen, auto-hide, linksonder */}
        <div
          style={{
            position: "absolute",
            left: 8,
            bottom: 8,
            display: showUi ? "flex" : "none",
            gap: 8,
          }}
        >
          {/* Mic-knop */}
                   <button
            type="button"
            style={btnCircle}
            onClick={() =>
              setMicOn((m) => {
                const next = !m;
                onMicChange?.(next); // 👈 App.js laten weten
                return next;
              })
            }
            title={micOn ? "Microfoon uit" : "Microfoon aan"}
          >

            <svg
              viewBox="0 0 24 24"
              style={{ width: 16, height: 16 }}
              aria-hidden="true"
            >
              {micOn ? (
                // mic aan
                <path
                  fill="currentColor"
                  d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.92V20h2a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2h2v-2.08A7 7 0 0 1 5 11a1 1 0 1 1 2 0 5 5 0 0 0 10 0z"
                />
              ) : (
                // mic uit (met streep)
                <path
                  fill="currentColor"
                  d="M4.21 4.21a1 1 0 0 1 1.42 0l2.1 2.1A3 3 0 0 1 15 6v3.59l4.2 4.2a1 1 0 0 1-1.4 1.42L16 13.41V14a4 4 0 0 1-6.8 2.8 4 4 0 0 1-1.2-2.8 1 1 0 0 0-2 0 6 6 0 0 0 9 5l2.59 2.6a1 1 0 0 1-1.42 1.4l-14-14a1 1 0 0 1 0-1.4zM12 4a3 3 0 0 1 3 3v2.59l-4.7-4.7A3 3 0 0 1 12 4z"
                />
              )}
            </svg>
          </button>

          {/* Cam-knop */}
          <button
            type="button"
            style={btnCircle}
            onClick={() => setCamOn((c) => !c)}
            title={camOn ? "Camera uit" : "Camera aan"}
          >
            <svg
              viewBox="0 0 24 24"
              style={{ width: 16, height: 16 }}
              aria-hidden="true"
            >
              {camOn ? (
                // camera aan
                <path
                  fill="currentColor"
                  d="M4 6a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1.38l3.16-1.9A1 1 0 0 1 20 6.38v11.24a1 1 0 0 1-1.84.52L15 16.62V18a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6zm2 0v12h7V6H6z"
                />
              ) : (
                // camera uit — zelfde vorm + streep
                <path
                  fill="currentColor"
                  d="M4.21 4.21a1 1 0 0 1 1.42 0L8 6.59V6a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3.59l1.38 1.38L20 8.38a1 1 0 0 1 1.5.86v8.52a1 1 0 0 1-1.5.86L17 16.62V18a2 2 0 0 1-2 2H9.41l-5.2 5.2a1 1 0 0 1-1.42-1.42l3-3V6a2 2 0 0 1 .42-1.79zM8 10.59V18h7v-1.38L8 10.59z"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Chat-knop, auto-hide, rechtsonder */}
        {typeof onToggleChat === "function" && (
          <div
            style={{
              position: "absolute",
              right: 8,
              bottom: 8,
              display: showUi ? "flex" : "none",
            }}
          >
            <button
              type="button"
              style={btnCircle}
              onClick={onToggleChat}
              title="Chat openen"
            >
              <svg
                viewBox="0 0 24 24"
                style={{ width: 16, height: 16 }}
                aria-hidden="true"
              >
                {/* simpel chat/tekstwolkje icoon */}
                <path
                  fill="currentColor"
                  d="M4 5a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H9.5L6 18.5V15H7a3 3 0 0 1-3-3V5zm3-1a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2v1.59L10.59 12H17a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1H7z"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
